import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { TenantDataService } from 'app/config/services/tenant-data.service';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { HelperService } from 'app/projects/shared/src/lib/services/helper';
import {
    CreatePasswordlessAccess,
    CreatePasswordlessAccessFailure,
    CreatePasswordlessAccessSuccess,
    DeletePasswordlessAccess,
    DeletePasswordlessAccessFailure,
    DeletePasswordlessAccessSuccess,
    FetchPasswordlessAccesses,
    FetchPasswordlessAccessesFailure,
    FetchPasswordlessAccessesSuccess,
    SendPasswordlessAccess,
    SendPasswordlessAccessFailure,
    SendPasswordlessAccessSuccess,
    UpdatePasswordlessAccess,
    UpdatePasswordlessAccessFailure,
    UpdatePasswordlessAccessSuccess,
} from '../../actions';
import { CreatePasswordlessAccessModel } from '../../models/create-passwordless-access';
import { EditPasswordlessAccessModel } from '../../models/edit-passwordless-access';
import { PasswordlessAccess } from '../../models/passwordless-access.model';
import { PasswordlessAccessState } from '../../models/passwordless-access.state';
import { PasswordlessAccessApiService } from './passwordless-access.api-service';

interface ClipboardItem {
    readonly types: string[];
    readonly presentationStyle: 'unspecified' | 'inline' | 'attachment';
    getType(): Promise<Blob>;
}

interface ClipboardItemData {
    [mimeType: string]: Blob | string | Promise<Blob | string>;
}

declare var ClipboardItem: {
    prototype: ClipboardItem;
    new (itemData: ClipboardItemData): ClipboardItem;
};

@Injectable()
export class PasswordlessAccessService {
    constructor(
        private _store: Store,
        private _actionHelper: NgxsActionHelper,
        private _helperService: HelperService,
        private _tenantDataService: TenantDataService,
        private _translateService: TranslateService,
        private _passwordlessAccessApiService: PasswordlessAccessApiService
    ) {}

    getPasswordlessAccesses(): PasswordlessAccess[] {
        return this._store.selectSnapshot(PasswordlessAccessState.getEntities);
    }

    fetchPasswordlessAccesses(pageSize: number, pageNumber: number, search: string): Promise<boolean> {
        this._store.dispatch(new FetchPasswordlessAccesses({ pageSize, pageNumber, search }));

        return this._actionHelper.race(FetchPasswordlessAccessesSuccess, FetchPasswordlessAccessesFailure).toPromise();
    }

    async createPasswordlessAccess(entity: CreatePasswordlessAccessModel): Promise<boolean> {
        this._store.dispatch(new CreatePasswordlessAccess(entity));

        return await this._actionHelper.race(CreatePasswordlessAccessSuccess, CreatePasswordlessAccessFailure).toPromise();
    }

    async updatePasswordlessAccess(entity: EditPasswordlessAccessModel): Promise<boolean> {
        this._store.dispatch(new UpdatePasswordlessAccess(entity));

        return await this._actionHelper.race(UpdatePasswordlessAccessSuccess, UpdatePasswordlessAccessFailure).toPromise();
    }

    async deletePasswordlessAccess(ids: string[]): Promise<boolean> {
        this._store.dispatch(new DeletePasswordlessAccess(ids));

        return await this._actionHelper.race(DeletePasswordlessAccessSuccess, DeletePasswordlessAccessFailure).toPromise();
    }

    async sendPasswordlessAccess(ids: string[], subject: string, message: string): Promise<boolean> {
        this._store.dispatch(new SendPasswordlessAccess({ accessIds: ids, subject, message }));

        return await this._actionHelper.race(SendPasswordlessAccessSuccess, SendPasswordlessAccessFailure).toPromise();
    }

    async copyQrCode(isChromium: boolean, passwordlessAccess: PasswordlessAccess): Promise<void> {
        if (isChromium) {
            try {
                let canvas = document.createElement('canvas');
                canvas.width = 250;
                canvas.height = 360;
                let ctx = canvas.getContext('2d');
                ctx.rect(0, 0, 250, 380);
                ctx.fillStyle = '#fff';
                ctx.fill();

                const logoImg = new Image();
                logoImg.src = this._tenantDataService.logo;
                logoImg.crossOrigin = 'anonymous';
                logoImg.onload = () => {
                    ctx.drawImage(logoImg, canvas.width / 2 - 80 / 2, 30, 80, (80 * logoImg.height) / logoImg.width);
                };

                ctx.font = '10px Roboto';
                ctx.textAlign = 'center';
                ctx.fillStyle = '#3B3E47';
                ctx.fillText(this._translateService.instant('NAV.ALL_USERS.NAV.PASSWORDLESS_ACCESS'), canvas.width / 2, 105);

                ctx.font = '14px Roboto';
                ctx.fillText(passwordlessAccess.user.fullName, canvas.width / 2, 125);

                let qrCodeImg = await this._passwordlessAccessApiService.fetchQrCode(passwordlessAccess.id);
                let qrCodeUrl = window.URL.createObjectURL(qrCodeImg);
                const qrImg = new Image();
                qrImg.src = qrCodeUrl;
                qrImg.crossOrigin = 'anonymous';
                qrImg.onload = () => {
                    ctx.drawImage(qrImg, canvas.width / 2 - 165 / 2, 135, 165, 165);
                };

                if (passwordlessAccess.accessCode) {
                    ctx.font = '10px Roboto';
                    ctx.fillText(this._translateService.instant('PASSWORDLESS_ACCESS.LABEL.ACCESS_CODE'), canvas.width / 2, 325);
                    ctx.font = '14px Roboto';
                    ctx.fillText(passwordlessAccess.accessCode, canvas.width / 2, 345);
                }

                setTimeout(() => {
                    canvas.toBlob(async function (blob) {
                        const item = new ClipboardItem({ 'image/png': blob });
                        (navigator.clipboard as any).write([item]);
                    });

                    this._helperService.showSnackBarMessage('GENERAL.MESSAGE.COPIED_CLIPBOARD');
                }, 500);
            } catch (err) {
                throw new Error(err.message);
            }
        } else {
            const textToCopy = `${
                passwordlessAccess.accessCode ? this._translateService.instant('PASSWORDLESS_ACCESS.LABEL.ACCESS_CODE') + ': ' + passwordlessAccess.accessCode + ',' : ''
            } URL: ${passwordlessAccess.url}`;
            navigator.clipboard.writeText(textToCopy).then(() => {
                this._helperService.showSnackBarMessage('GENERAL.MESSAGE.COPIED_CLIPBOARD');
            });
        }
    }
}
